import React, { useState, useEffect } from 'react';
import './GradingJourney.css';
import { gradingSyllabus, BeltSyllabus } from '../data/gradingSyllabus';

import { SyllabusSection } from '../data/syllabus_grades/types';

type Tab = 'theoryUnder13' | 'theoryOver13' | 'practical';

// Helper component to render syllabus tables
const SyllabusRender: React.FC<{ sections: SyllabusSection[] }> = ({ sections }) => {
  if (!sections || sections.length === 0) {
    return <p>Content coming soon...</p>;
  }

  return (
    <div className="syllabus-content">
      {sections.map((section, index) => (
        <div key={index} className="syllabus-section">
          {/* {section.title && <h3 className="section-title">{section.title}</h3>} */}
          <table className="syllabus-table">
            <thead>
              <tr>
                <th colSpan={2} style={{ backgroundColor: '#a8d0e6', textAlign: 'center' }}>
                  {section.title || 'Requirements'}
                  {section.subTitle && (
                    <div style={{ fontSize: '0.9em', fontWeight: 'normal', marginTop: '4px', color: '#555' }}>
                      {Array.isArray(section.subTitle) ? (
                        <ul style={{ margin: 0, padding: 0, listStyleType: 'none', textAlign: 'center' }}>
                          {section.subTitle.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        section.subTitle
                      )}
                    </div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {section.items.map((item, idx) => {
                const isAnswerEmpty = !item.answer || item.answer === ' ' || (Array.isArray(item.answer) && item.answer.length === 0);
                return (
                  <tr key={idx}>
                    <td colSpan={isAnswerEmpty ? 2 : 1} style={isAnswerEmpty ? { textAlign: item.overrideTextLocation || 'center', fontWeight: 'normal' } : {}}>
                      {item.question}
                    </td>
                    {!isAnswerEmpty && (
                      <td>
                        {item.isImage ? (
                          <img src={item.answer as string} alt={item.question} style={{ maxHeight: '100px', display: 'block' }} />
                        ) : Array.isArray(item.answer) ? (
                          item.answer.map((line, i) => (
                            <div key={i}>
                              {line}
                              {i < item.answer.length - 1 && <br />}
                            </div>
                          ))
                        ) : (
                          item.answer
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const GradingJourney: React.FC = () => {
  const [selectedBeltId, setSelectedBeltId] = useState<string>(gradingSyllabus[0].id);
  const [activeTab, setActiveTab] = useState<Tab>('theoryUnder13');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contentRef = React.useRef<HTMLHeadingElement>(null);

  const selectedBelt: BeltSyllabus = gradingSyllabus.find(b => b.id === selectedBeltId) || gradingSyllabus[0];

  useEffect(() => {
    // Ensure activeTab is valid for the selected belt
    const hasUnder13 = selectedBelt.theoryUnder13 && selectedBelt.theoryUnder13.length > 0;
    const hasOver13 = selectedBelt.theoryOver13 && selectedBelt.theoryOver13.length > 0;

    if (activeTab === 'theoryUnder13' && !hasUnder13) {
      if (hasOver13) setActiveTab('theoryOver13');
      else setActiveTab('practical');
    } else if (activeTab === 'theoryOver13' && !hasOver13) {
      if (hasUnder13) setActiveTab('theoryUnder13');
      else setActiveTab('practical');
    }
  }, [selectedBelt, activeTab]);

  const handleBeltSelect = (id: string) => {
    setSelectedBeltId(id);

    // Check if mobile (width <= 900px matches CSS breakpoint)
    if (window.innerWidth <= 900 && contentRef.current) {
      // Small timeout to allow state update and layout check if needed, 
      // though scrollIntoView usually handles it. 
      // Using { behavior: 'smooth' } for nicer effect.
      // Adjust scroll position slightly (-100px) for fixed headers if any, 
      // but standard scrollIntoView is a good start.
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const hasUnder13 = selectedBelt.theoryUnder13 && selectedBelt.theoryUnder13.length > 0;
  const hasOver13 = selectedBelt.theoryOver13 && selectedBelt.theoryOver13.length > 0;

  return (
    <section className="grading-journey-section">
      <div className="container">
        <h1 className="page-title">Grading Journey</h1>
        <p className="page-subtitle">
          Select a belt level to view the required theory and practical syllabus.
        </p>

        <div className="journey-content-wrapper">
          {/* Sidebar / Top bar for Belt Selection */}
          <div className="belt-selector">
            {gradingSyllabus.map((belt) => (
              <button
                key={belt.id}
                className={`belt-selector-item ${selectedBeltId === belt.id ? 'active' : ''}`}
                onClick={() => handleBeltSelect(belt.id)}
              >
                <span
                  className="belt-color-indicator"
                  style={{ backgroundColor: belt.color, border: belt.color === '#ffffff' ? '1px solid #ccc' : 'none' }}
                />
                <span className="belt-name-label">{belt.name}</span>
              </button>
            ))}
          </div>

          {/* Main Content Area */}
          <div className="belt-details-card">
            <h2
              ref={contentRef}
              className="selected-belt-title"
              style={{ borderBottomColor: selectedBelt.color === '#ffffff' ? '#ccc' : selectedBelt.color }}
            >
              {selectedBelt.name}
            </h2>

            {/* Tabs */}
            <div className="syllabus-tabs">
              {hasUnder13 && (
                <button
                  className={`tab-button ${activeTab === 'theoryUnder13' ? 'active' : ''}`}
                  onClick={() => setActiveTab('theoryUnder13')}
                >
                  Under 13 Theory
                </button>
              )}
              {hasOver13 && (
                <button
                  className={`tab-button ${activeTab === 'theoryOver13' ? 'active' : ''}`}
                  onClick={() => setActiveTab('theoryOver13')}
                >
                  {/* for Blue belt and above, display "Theory" instead of "Over 13 Theory" */}
                  {['blue', 'blue-red', 'red', 'red-black', 'black', 'black-1st', 'black-2nd'].includes(selectedBelt.id) ? 'Theory' : 'Over 13 Theory'}
                </button>
              )}
              <button
                className={`tab-button ${activeTab === 'practical' ? 'active' : ''}`}
                onClick={() => setActiveTab('practical')}
              >
                Practical
              </button>
            </div>

            {/* Content Display */}
            <div className="syllabus-content-wrapper">
              {activeTab === 'theoryUnder13' && (
                <SyllabusRender sections={selectedBelt.theoryUnder13 || []} />
              )}
              {activeTab === 'theoryOver13' && (
                <SyllabusRender sections={selectedBelt.theoryOver13 || []} />
              )}
              {activeTab === 'practical' && (
                <SyllabusRender sections={selectedBelt.practical} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GradingJourney;
